/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Icon, Button, EmptyState, Modal, FilterPanel, FilterSelect, Search, BadgeCounter } from '@mdsol/onex-design'
// @ts-expect-error - createColumnHelper is exported but not in TypeScript definitions
import { DataGrid, createColumnHelper } from '@mdsol/onex-design/DataGrid'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../../root'
import PropertySearchBulkActionHeader from '../../../browser/PropertySearchBulkActionHeader'
import { USER_ROLE } from '../../../../constants/user'
import { PAGE_SIZES_ARRAY, DEFAULT_PAGE_SIZE } from '../../../../constants/pagination'
import {
  getLinkedEntities,
  propertyValuesSearch,
  getSelectionsForEtmfReports,
  getStudyDocumentsForOverviewPropertyBased
} from '../../../../services/api/etmf'
import { transformToSelectOptions } from '~/utils/selectOptions'
import { toast } from '~/utils/toast'
import {
  addDocumentRefs,
  getFilePath,
  type PathItem,
  type StudyDocumentRequestDTO
} from '../../../../services/api/files'
import { useEsignature } from '../../../../hooks/useEsignature'
import { MedidataLogo } from '../../../../components/MedidataLogo'
import { buildUrlWithCSelections } from '~/utils/navigation'
import styles from './AddReferences.module.css'

export interface AvailableReference {
  id: number
  name: string
  artifact: string
  product: string
  study: string
  environment: string
  country: string
  site: string
}

const MAX_REFERENCES = 100

// Roles that cannot add/modify references
const READ_ONLY_ROLES = [
  USER_ROLE.AUDITOR,
  USER_ROLE.INSPECTOR,
  USER_ROLE.ETMF_STUDY_USER,
  USER_ROLE.ETMF_SITE_USER,
  USER_ROLE.ETMF_SPONSOR
]

/**
 * Parse eTMF context from file path using keyword-based extraction.
 * Product: element immediately after "Clinical"
 * Study: element after "Studies"
 * Environment: element after "Environments"
 * Country: element after "Countries"
 * Site: element after "Sites"
 */
function parsePathContext(pathItems: PathItem[]): {
  product?: string
  study?: string
  environment?: string
  country?: string
  site?: string
} {
  const names = pathItems.map((p) => p.name)
  const ctx: { product?: string; study?: string; environment?: string; country?: string; site?: string } = {}

  for (let i = 0; i < names.length; i++) {
    const lower = names[i].toLowerCase()
    if (lower === 'clinical' && i + 1 < names.length) {
      ctx.product = names[i + 1]
    } else if (lower === 'studies' && i + 1 < names.length) {
      ctx.study = names[i + 1]
    } else if (lower === 'environments' && i + 1 < names.length) {
      ctx.environment = names[i + 1]
    } else if (lower === 'countries' && i + 1 < names.length) {
      ctx.country = names[i + 1]
    } else if (lower === 'sites' && i + 1 < names.length) {
      ctx.site = names[i + 1]
    }
  }

  return ctx
}

interface AddReferencesModalProps {
  /** The base document's file ID */
  fsiId: number
  /** Whether the base document resides within an eTMF folder (pre-populates context filters) */
  etmfContext?: {
    product?: string
    study?: string
    environment?: string
    country?: string
    site?: string
  }
  /** Whether the user has permission to manage references */
  canManageReferences: boolean
  /** Whether the "Allow Editing of Property Values within Edit and Review Phase" setting is enabled */
  allowEditInWorkflow?: boolean
  /** Whether the "require e-sign for file commands" policy setting is enabled */
  requireESign?: boolean
  /** Already-linked reference IDs from parent references list */
  existingLinkedIds?: number[]
  /** Callback when user confirms selection */
  onConfirm: (selectedReferences: AvailableReference[]) => void
  /** Callback when modal closes */
  onClose?: () => void
}

const columnHelper = createColumnHelper()

/**
 * Extract only the keys that are `true` into a Set for efficient comparison
 * Used for O(N) equality checks instead of deep object comparison
 */
const getActiveKeys = (selectionObj: Record<string, boolean> | undefined): Set<string> => {
  const active = new Set<string>()
  if (!selectionObj) return active
  for (const key in selectionObj) {
    if (selectionObj[key]) active.add(key)
  }
  return active
}

/**
 * O(N) equality check between two Sets
 * More efficient than deep object comparison for selection persistence
 */
const areSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false
  let equal = true
  a.forEach((item) => {
    if (!b.has(item)) equal = false
  })
  return equal
}

const getCriteriaKey = (criteria: StudyDocumentRequestDTO | null) => {
  if (!criteria) return null
  return JSON.stringify(criteria)
}

const AddReferencesModal: React.FC<AddReferencesModalProps> = ({
  fsiId,
  etmfContext,
  canManageReferences,
  allowEditInWorkflow = true,
  requireESign = false,
  existingLinkedIds,
  onConfirm,
  onClose
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  // hideModal is handled by ModalContainer's injected onClose
  const { signAction } = useEsignature()

  const [isOpen, setIsOpen] = useState(true)

  // RBAC check
  const currentRole = context.service.auth.getRole()
  const isReadOnly = !canManageReferences || READ_ONLY_ROLES.includes(currentRole as string)

  // Data state
  const [references, setReferences] = useState<AvailableReference[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Pagination
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([])

  // Row selection persistence across pages
  // rowSelectionByPages stores selections per page: { 0: { '123': true }, 1: { '456': true } }
  const [rowSelectionByPages, setRowSelectionByPages] = useState<Record<number, Record<string, boolean>>>({})
  const [isAllDataSelected, setIsAllDataSelected] = useState(false)

  // Flattened row selection object from all pages — passed to DataGrid's state.rowSelection
  // resyncTrigger forces a new object reference when DataGrid internally clears selections
  // (on pagination/sort change). Without it, the useMemo returns the same reference,
  // and DataGridTable's sync effect won't re-fire.
  const [resyncTrigger, setResyncTrigger] = useState(0)
  const allRowSelectionObj = useMemo(() => {
    const merged: Record<string, boolean> = {}
    Object.values(rowSelectionByPages).forEach((pageSelection) => {
      Object.entries(pageSelection).forEach(([key, value]) => {
        if (value) merged[key] = true
      })
    })
    return merged
  }, [rowSelectionByPages, resyncTrigger])

  // Ref to always have the latest allRowSelectionObj for comparison in callbacks
  // without adding it as a dependency (which would cause unnecessary callback ref changes)
  const allRowSelectionRef = useRef(allRowSelectionObj)
  allRowSelectionRef.current = allRowSelectionObj

  // Track selected items' details (id + name) across pages for use in confirmations
  const selectedItemsMapRef = useRef<Map<number, { id: number; name: string }>>(new Map())

  // Convert row selection from ALL pages to array of selected FSI IDs
  const selectedIds = useMemo(() => {
    return Object.keys(allRowSelectionObj).map((key) => Number(key))
  }, [allRowSelectionObj])

  // Flag to distinguish DataGrid's forced setRowSelection({}) on pagination/sort change
  // from genuine user uncheck. Set true in onChangeControl, consumed in handleRowSelectionChange.
  const dgInternalClearRef = useRef(false)

  // Debounce ref prevents spurious rapid-fire state changes from onAllDataSelected
  const allDataSelectedAtRef = useRef<number>(0)

  // Total elements from server for pagination
  const totalElementsRef = useRef<number>(0)

  // Inline error banner for exceeding max references
  const [referenceError, setReferenceError] = useState<string | null>(null)
  const [localExistingLinkedIds, setLocalExistingLinkedIds] = useState<number[]>(existingLinkedIds ?? [])

  useEffect(() => {
    setLocalExistingLinkedIds(existingLinkedIds ?? [])
  }, [existingLinkedIds])

  const existingReferenceIds = useMemo(() => new Set(localExistingLinkedIds), [localExistingLinkedIds])

  // Update rowSelectionByPages when user interacts with checkboxes.
  // DataGrid fires onRowSelectionChange with the full merged selection (all pages),
  // so we extract only the current page's portion and store it.
  //
  // CRITICAL: DataGrid internally calls setRowSelection({}) on every pagination/sort change.
  // This forced clear propagates here as onRowSelectionChange({}). We detect it via
  // dgInternalClearRef (set in onChangeControl/sorting handlers) and skip + resync.
  const handleRowSelectionChange = useCallback(
    (newSelection: Record<string, boolean>) => {
      const currentAll = allRowSelectionRef.current

      // Efficiently grab active keys as Sets
      const newActiveKeys = getActiveKeys(newSelection)
      const curActiveKeys = getActiveKeys(currentAll)

      // If identical → spurious sync or re-render echo → skip
      if (areSetsEqual(newActiveKeys, curActiveKeys)) return

      // If DataGrid is clearing due to internal pagination/sort change → skip + resync
      if (dgInternalClearRef.current) {
        dgInternalClearRef.current = false
        setResyncTrigger((prev) => prev + 1)
        return
      }

      // Built-in "Clear Selection" button: empty incoming + we have selections + not internal clear.
      // Wipe selections across ALL pages (not just current).
      if (newActiveKeys.size === 0 && curActiveKeys.size > 0) {
        setRowSelectionByPages({})
        selectedItemsMapRef.current.clear()
        setIsAllDataSelected(false)
        return
      }

      // Genuine user interaction (check/uncheck). Extract current page's portion using the Set.
      const newPagePortion: Record<string, boolean> = {}
      references.forEach((ref) => {
        const id = String(ref.id)
        if (newActiveKeys.has(id)) {
          newPagePortion[id] = true
        }
      })

      // Update selectedItemsMap: add newly selected items from current page entries
      references.forEach((ref) => {
        const id = Number(ref.id)
        if (newPagePortion[String(id)]) {
          selectedItemsMapRef.current.set(id, { id, name: ref.name })
        } else {
          selectedItemsMapRef.current.delete(id)
        }
      })

      // Only update state if the page portion actually changed.
      // Without this guard, DataGrid re-emitting stale off-page selections after
      // a bulk clear would create new state references (even for empty page portions),
      // causing infinite re-render loops.
      setRowSelectionByPages((prev) => {
        const existingPageKeys = getActiveKeys(prev[pagination.pageIndex])
        const newPageKeys = getActiveKeys(newPagePortion)

        if (areSetsEqual(existingPageKeys, newPageKeys)) return prev

        // Clean up empty page entries when the user unchecked everything on this page
        if (newPageKeys.size === 0) {
          const nextState = { ...prev }
          delete nextState[pagination.pageIndex]
          return nextState
        }

        return { ...prev, [pagination.pageIndex]: newPagePortion }
      })
    },
    [pagination.pageIndex, references]
  )

  // Debounced handler for onAllDataSelected to ignore spurious rapid-fire false signals
  const handleOnAllDataSelected = useCallback((isAllSelected: boolean) => {
    if (isAllSelected) {
      allDataSelectedAtRef.current = Date.now()
      setIsAllDataSelected(true)
    } else {
      // Ignore spurious false within 100ms of a true
      if (Date.now() - allDataSelectedAtRef.current < 100) {
        return
      }
      allDataSelectedAtRef.current = 0
      setIsAllDataSelected(false)
    }
  }, [])

  // Wrap selection change to track updates
  // Uses refs instead of state deps so the callback identity is stable (prevents DataGrid
  // internal useEffect from firing on every render when isAllDataSelected changes).
  // NOTE: This is now integrated into handleRowSelectionChange above

  // Handler for "Clear selection" bulk action
  const handleClearSelection = useCallback(() => {
    // Update ref immediately so DataGrid's stale onRowSelectionChange callback
    // (which may fire with old selection before React commits our state update)
    // sees an empty ref and the identity check skips it.
    allRowSelectionRef.current = {}
    setRowSelectionByPages({})
    selectedItemsMapRef.current.clear()
    setIsAllDataSelected(false)
  }, [])

  const [searchValue, setSearchValue] = useState('')
  const [appliedSearchCriteria, setAppliedSearchCriteria] = useState<StudyDocumentRequestDTO | null>(null)
  const [fullyLinkedCriteriaKey, setFullyLinkedCriteriaKey] = useState<string | null>(null)

  // Context filter states (store full option objects with fsiId, fsiPropName for API payload)
  const [productFilter, setProductFilter] = useState<any>(null)
  const [studyFilter, setStudyFilter] = useState<any>(null)
  const [environmentFilter, setEnvironmentFilter] = useState<any>(null)
  const [countryFilter, setCountryFilter] = useState<any>(null)
  const [siteFilter, setSiteFilter] = useState<any>(null)
  const [artifactFilter, setArtifactFilter] = useState<any[]>([])
  const [totalElements, setTotalElements] = useState(0)

  // Path-derived eTMF context (internal fetch, keyword-based extraction)
  const pathContextRef = useRef<
    | {
        product?: string
        study?: string
        environment?: string
        country?: string
        site?: string
      }
    | undefined
  >(etmfContext)
  const [pathLoaded, setPathLoaded] = useState(false)
  const isInitializingContextRef = useRef(false)

  // Fetch file path on mount and derive eTMF context
  useEffect(() => {
    getFilePath(fsiId)
      .then((pathItems) => {
        const isClinical = pathItems.some((item) => item.name.toLowerCase().includes('clinical'))
        if (isClinical) {
          const ctx = parsePathContext(pathItems)
          pathContextRef.current = ctx
          isInitializingContextRef.current = !!ctx.product
        }
      })
      .catch(() => {
        // Not an eTMF document or path unavailable — leave context as-is
      })
      .finally(() => {
        setPathLoaded(true)
      })
  }, [fsiId])

  // Filter dropdown options loaded from API (cascading)
  const [filterOptions, setFilterOptions] = useState<{
    product: any[]
    study: any[]
    environment: any[]
    country: any[]
    site: any[]
  }>({ product: [], study: [], environment: [], country: [], site: [] })

  // eTMF hierarchy level constants
  const Constants = (context as any).constant
  const { PRODUCT, STUDY, ENVIRONMENT, COUNTRY, SITE } = Constants.etmf.level

  // Load product options after path is loaded
  useEffect(() => {
    if (!pathLoaded) return
    const ctx = pathContextRef.current
    getSelectionsForEtmfReports(PRODUCT, undefined, true)
      .then((products) => {
        const productOpts = transformToSelectOptions(products, 'fsiId', 'fsiName', null, true)
        setFilterOptions((prev) => ({ ...prev, product: productOpts }))

        // Auto-select matching product from path context
        if (isInitializingContextRef.current && ctx?.product) {
          const match = productOpts.find((opt: any) => opt.label === ctx.product || opt.fsiPropName === ctx.product)
          if (match) {
            setProductFilter(match)
          } else {
            isInitializingContextRef.current = false
          }
        }
      })
      .catch((error) => {
        console.error('[AddReferencesModal] Error loading products:', error)
      })
  }, [pathLoaded])

  // Cascade: load studies when product changes
  useEffect(() => {
    if (!productFilter?.value) {
      setFilterOptions((prev) => ({ ...prev, study: [], environment: [], country: [], site: [] }))
      setStudyFilter(null)
      setEnvironmentFilter(null)
      setCountryFilter(null)
      setSiteFilter(null)
      return
    }
    getSelectionsForEtmfReports(STUDY, productFilter.value, true)
      .then((studies) => {
        const studyOpts = transformToSelectOptions(studies, 'fsiId', 'fsiName', null, true)
        setFilterOptions((prev) => ({ ...prev, study: studyOpts, environment: [], country: [], site: [] }))
        if (isInitializingContextRef.current && pathContextRef.current?.study) {
          const match = studyOpts.find(
            (opt: any) =>
              opt.label === pathContextRef.current?.study || opt.fsiPropName === pathContextRef.current?.study
          )
          if (match) {
            setStudyFilter(match)
          } else {
            isInitializingContextRef.current = false
          }
        } else {
          setStudyFilter(null)
          setEnvironmentFilter(null)
          setCountryFilter(null)
          setSiteFilter(null)
        }
      })
      .catch((error) => {
        console.error('[AddReferencesModal] Error loading studies:', error)
      })
  }, [productFilter?.value])

  // Cascade: load environments when study changes
  useEffect(() => {
    if (!studyFilter?.value) {
      setFilterOptions((prev) => ({ ...prev, environment: [], country: [], site: [] }))
      setEnvironmentFilter(null)
      setCountryFilter(null)
      setSiteFilter(null)
      return
    }
    getSelectionsForEtmfReports(ENVIRONMENT, studyFilter.value, true)
      .then((environments) => {
        const envOpts = transformToSelectOptions(environments, 'fsiId', 'fsiName', null, true)
        setFilterOptions((prev) => ({ ...prev, environment: envOpts, country: [], site: [] }))
        if (isInitializingContextRef.current && pathContextRef.current?.environment) {
          const match = envOpts.find(
            (opt: any) =>
              opt.label === pathContextRef.current?.environment ||
              opt.fsiPropName === pathContextRef.current?.environment
          )
          if (match) {
            setEnvironmentFilter(match)
          } else {
            isInitializingContextRef.current = false
          }
        } else {
          setEnvironmentFilter(null)
          setCountryFilter(null)
          setSiteFilter(null)
        }
      })
      .catch((error) => {
        console.error('[AddReferencesModal] Error loading environments:', error)
      })
  }, [studyFilter?.value])

  // Cascade: load countries when environment changes
  useEffect(() => {
    if (!environmentFilter?.value) {
      setFilterOptions((prev) => ({ ...prev, country: [], site: [] }))
      setCountryFilter(null)
      setSiteFilter(null)
      return
    }
    getSelectionsForEtmfReports(COUNTRY, environmentFilter.value, true)
      .then((countries) => {
        const countryOpts = transformToSelectOptions(countries, 'fsiId', 'fsiName', null, true)
        setFilterOptions((prev) => ({ ...prev, country: countryOpts, site: [] }))
        if (isInitializingContextRef.current && pathContextRef.current?.country) {
          const match = countryOpts.find(
            (opt: any) =>
              opt.label === pathContextRef.current?.country || opt.fsiPropName === pathContextRef.current?.country
          )
          if (match) {
            setCountryFilter(match)
          } else {
            isInitializingContextRef.current = false
          }
        } else {
          setCountryFilter(null)
          setSiteFilter(null)
        }
      })
      .catch((error) => {
        console.error('[AddReferencesModal] Error loading countries:', error)
      })
  }, [environmentFilter?.value])

  // Cascade: load sites when country changes
  useEffect(() => {
    if (!countryFilter?.value) {
      setFilterOptions((prev) => ({ ...prev, site: [] }))
      setSiteFilter(null)
      return
    }
    getSelectionsForEtmfReports(SITE, countryFilter.value, true)
      .then((sites) => {
        const siteOpts = transformToSelectOptions(sites, 'fsiId', 'fsiName', null, true)
        setFilterOptions((prev) => ({ ...prev, site: siteOpts }))
        if (isInitializingContextRef.current && pathContextRef.current?.site) {
          const match = siteOpts.find(
            (opt: any) => opt.label === pathContextRef.current?.site || opt.fsiPropName === pathContextRef.current?.site
          )
          if (match) {
            setSiteFilter(match)
          }
          // Site is the last level — initialization complete
          isInitializingContextRef.current = false
        } else {
          if (isInitializingContextRef.current) {
            // No site in context — initialization complete at this level
            isInitializingContextRef.current = false
          }
          setSiteFilter(null)
        }
      })
      .catch((error) => {
        console.error('[AddReferencesModal] Error loading sites:', error)
      })
  }, [countryFilter?.value])

  // Artifact options — loaded from API (matching Summary.tsx pattern)
  const [artifactProperties, setArtifactProperties] = useState<{
    usedValues: any[]
    values: any[]
  }>({ usedValues: [], values: [] })

  useEffect(() => {
    getLinkedEntities()
      .then((result) => {
        const entityLinkMap = result?.entityLinkMap
        if (!entityLinkMap?.ARTIFACT) return

        const artifactId =
          typeof entityLinkMap.ARTIFACT === 'string' ? parseInt(entityLinkMap.ARTIFACT, 10) : entityLinkMap.ARTIFACT

        // Load used values via propertyValuesSearch
        propertyValuesSearch(artifactId).then((usedValues: any[]) => {
          setArtifactProperties((prev) => ({ ...prev, usedValues }))
        })

        // Load definition options via metadata.getById
        context.api.metadata
          .getById(artifactId)
          .then((metaData: any) => {
            if (metaData?.definition?.options) {
              setArtifactProperties((prev) => ({ ...prev, values: metaData.definition.options }))
            }
          })
          .catch((error) => {
            console.error('[AddReferencesModal] Error loading artifact metadata:', error)
          })
      })
      .catch((error) => {
        console.error('[AddReferencesModal] Error loading linked entities:', error)
      })
  }, [])

  // Build artifact options from API data (matching Summary.tsx)
  const artifactOptions = useMemo(() => {
    const usedValues = artifactProperties.usedValues || []
    const definitionValues = artifactProperties.values || []

    const allOptions = [...usedValues, ...definitionValues].map((item: any) => ({
      value: item.value || item,
      label: item.label || item.value || item
    }))

    // Deduplicate by value
    const uniqueOptions = allOptions.reduce((acc: any[], curr: any) => {
      if (!acc.find((opt) => opt.value === curr.value)) {
        acc.push(curr)
      }
      return acc
    }, [])

    return uniqueOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)))
  }, [artifactProperties.usedValues, artifactProperties.values])

  // Build API request payload (matching Summary.tsx buildPayloadForStudyDocuments)
  const buildPayload = useCallback(
    (criteria?: {
      productFilter?: any
      studyFilter?: any
      environmentFilter?: any
      countryFilter?: any
      siteFilter?: any
      artifactFilter?: any[]
      searchValue?: string
    }) => {
      let fsiId: string | number | null = null
      const DTO: any = {}

      const currentProductFilter = criteria?.productFilter ?? productFilter
      const currentStudyFilter = criteria?.studyFilter ?? studyFilter
      const currentEnvironmentFilter = criteria?.environmentFilter ?? environmentFilter
      const currentCountryFilter = criteria?.countryFilter ?? countryFilter
      const currentSiteFilter = criteria?.siteFilter ?? siteFilter
      const currentArtifactFilter = criteria?.artifactFilter ?? artifactFilter
      const currentSearchValue = criteria?.searchValue ?? searchValue

      if (currentProductFilter) {
        DTO.product = currentProductFilter.fsiPropName
        fsiId = currentProductFilter.fsiId
      }
      if (currentStudyFilter) {
        DTO.study = currentStudyFilter.fsiPropName
        fsiId = currentStudyFilter.fsiId
      }
      if (currentEnvironmentFilter) {
        DTO.environment = currentEnvironmentFilter.fsiPropName
        fsiId = currentEnvironmentFilter.fsiId
      }
      if (currentCountryFilter) {
        DTO.country = currentCountryFilter.fsiPropName || currentCountryFilter.fsiName
        fsiId = currentCountryFilter.fsiId
      }
      if (currentSiteFilter) {
        DTO.site = currentSiteFilter.fsiPropName || currentSiteFilter.fsiName
        fsiId = currentSiteFilter.fsiId
      }

      // Artifact filter (array of artifact values)
      if (currentArtifactFilter && Array.isArray(currentArtifactFilter) && currentArtifactFilter.length) {
        DTO.artifacts = currentArtifactFilter.map((art: any) => art.value || art)
      }

      // Search filter (fsiName)
      const sv = currentSearchValue
      if (sv && sv.trim()) {
        DTO.fsiName = sv.trim()
      }

      DTO.type = 'COMPLETED'
      DTO.userId = context.service.auth.getUserId()
      DTO.documentReference = true

      // fsiId logic: use deepest selected eTMF dropdown, or 1 if none selected but searching
      const noEtmfSelected =
        !currentProductFilter &&
        !currentStudyFilter &&
        !currentEnvironmentFilter &&
        !currentCountryFilter &&
        !currentSiteFilter

      if (noEtmfSelected) {
        DTO.fsiId = 1
      } else {
        DTO.fsiId = fsiId
      }

      return DTO
    },
    [
      productFilter,
      studyFilter,
      environmentFilter,
      countryFilter,
      siteFilter,
      artifactFilter,
      searchValue,
      context.service.auth
    ]
  )

  // Fetch documents from API (server-side pagination)
  // Reads pagination/sorting from state — called reactively via useEffect.
  const fetchDocuments = useCallback(async () => {
    if (!appliedSearchCriteria) {
      return
    }

    setIsLoading(true)
    try {
      const request = appliedSearchCriteria
      const sortParam = sorting.length > 0 ? sorting[0] : undefined

      const data = await getStudyDocumentsForOverviewPropertyBased(request, {
        pageStart: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortBy: sortParam?.id || 'name',
        sortDesc: sortParam?.desc ?? false,
        excludeFsiId: fsiId
      })
      setTotalElements(data?.totalElements ?? 0)

      const mapped: AvailableReference[] = (data?.elements ?? []).map((item: any) => ({
        id: item?.fsiId || 0,
        name: item?.fsiName || '-',
        artifact: item?.fsiArtifact || '-',
        product: item?.fsiProduct || '-',
        study: item?.fsiStudy || '-',
        environment: item?.fsiEnv || '-',
        country: item?.fsiCountry || '-',
        site: item?.fsiSite || '-'
      }))

      totalElementsRef.current = data?.totalElements ?? 0
      setReferences(mapped)
      setHasSearched(true)
    } catch (error: any) {
      console.error('[AddReferencesModal] Failed to fetch documents:', error)

      // Use API error message directly
      const errorMessage = error?.data?.message || error?.message || 'ERROR'
      toast.error(context.service.filter.translate(errorMessage))
      setReferences([])
      totalElementsRef.current = 0
    } finally {
      setIsLoading(false)
    }
  }, [appliedSearchCriteria, sorting, pagination.pageIndex, pagination.pageSize])

  // Stable ref to avoid callback identity cascade from buildPayload depending on searchValue.
  const fetchDocumentsRef = useRef(fetchDocuments)
  fetchDocumentsRef.current = fetchDocuments

  // Re-fetch when pagination or sorting changes after initial search.
  useEffect(() => {
    if (hasSearched) {
      fetchDocumentsRef.current()
    }
  }, [pagination.pageIndex, pagination.pageSize, sorting, hasSearched])

  // Execute search (triggered by Apply button)
  const executeSearch = useCallback(() => {
    const nextAppliedCriteria = buildPayload({
      productFilter,
      studyFilter,
      environmentFilter,
      countryFilter,
      siteFilter,
      artifactFilter,
      searchValue
    }) as StudyDocumentRequestDTO

    setRowSelectionByPages({})
    selectedItemsMapRef.current.clear()
    setIsAllDataSelected(false)
    setAppliedSearchCriteria(nextAppliedCriteria)
    setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }))
    // Toggle hasSearched off→on to ensure the effect fires even when already at page 0
    setHasSearched(false)
    queueMicrotask(() => setHasSearched(true))
  }, [
    buildPayload,
    productFilter,
    studyFilter,
    environmentFilter,
    countryFilter,
    siteFilter,
    artifactFilter,
    searchValue
  ])

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
    setIsAllDataSelected(false)
    const hasOtherFilters = !!(
      productFilter ||
      studyFilter ||
      environmentFilter ||
      countryFilter ||
      siteFilter ||
      (artifactFilter && artifactFilter.length > 0)
    )
    if (hasOtherFilters) {
      executeSearch()
    } else {
      setReferences([])
      setHasSearched(false)
      setAppliedSearchCriteria(null)
      totalElementsRef.current = 0
    }
  }, [executeSearch, productFilter, studyFilter, environmentFilter, countryFilter, siteFilter, artifactFilter])

  const handleApply = useCallback(() => {
    executeSearch()
  }, [executeSearch])

  const handleClearFilters = useCallback(() => {
    setProductFilter(null)
    setProductFilter(null)
    setStudyFilter(null)
    setEnvironmentFilter(null)
    setCountryFilter(null)
    setSiteFilter(null)
    setArtifactFilter([])
    setRowSelectionByPages({})
    selectedItemsMapRef.current.clear()
    setIsAllDataSelected(false)
    // Keep the search text. Re-run with just the text, or reset to empty state
    // if there's nothing left to search on.
    if (searchValue.trim()) {
      executeSearch()
    } else {
      setReferences([])
      setHasSearched(false)
      setAppliedSearchCriteria(null)
      totalElementsRef.current = 0
    }
  }, [searchValue, executeSearch])

  const handleFilterChange = useCallback(
    (setter: (val: any) => void) => (option: any) => {
      const selected = Array.isArray(option) ? (option[0] ?? null) : (option ?? null)
      setter(selected)
      // Clear selection when any filter changes
      setRowSelectionByPages({})
      selectedItemsMapRef.current.clear()
      setIsAllDataSelected(false)
    },
    []
  )

  const handleSelectAllForCurrentCriteria = useCallback(() => {
    // Select all selectable rows on the current page (mirrors library's handleSelectAllRows)
    // Uses same conditions as row selectability: exclude existing linked references
    const currentPageSelection: Record<string, boolean> = {}
    references.forEach((ref) => {
      if (!existingReferenceIds.has(ref.id)) {
        currentPageSelection[String(ref.id)] = true
      }
    })

    // Update selectedItemsMap with the newly bulk-selected items
    references.forEach((ref) => {
      if (currentPageSelection[String(ref.id)]) {
        selectedItemsMapRef.current.set(ref.id, { id: ref.id, name: ref.name })
      }
    })

    // Update per-page selections and mark all data as selected
    setRowSelectionByPages((prev) => ({
      ...prev,
      [pagination.pageIndex]: currentPageSelection
    }))

    // Snapshot current filter/search criteria when selecting all
    const nextAppliedCriteria = buildPayload({
      productFilter,
      studyFilter,
      environmentFilter,
      countryFilter,
      siteFilter,
      artifactFilter,
      searchValue
    }) as StudyDocumentRequestDTO

    setAppliedSearchCriteria(nextAppliedCriteria)
    setIsAllDataSelected(true)
    setHasSearched(true)
  }, [
    buildPayload,
    pagination.pageIndex,
    productFilter,
    studyFilter,
    environmentFilter,
    countryFilter,
    siteFilter,
    artifactFilter,
    searchValue,
    references,
    existingReferenceIds
  ])

  const selectedReferences = useMemo(() => {
    return references.filter((ref) => allRowSelectionObj[String(ref.id)] && !existingReferenceIds.has(ref.id))
  }, [allRowSelectionObj, references, existingReferenceIds])

  const currentCriteriaKey = useMemo(() => getCriteriaKey(appliedSearchCriteria), [appliedSearchCriteria])
  const isCurrentCriteriaFullyLinked = !!currentCriteriaKey && fullyLinkedCriteriaKey === currentCriteriaKey

  const selectedCount = useMemo(() => {
    return Object.keys(allRowSelectionObj).filter(
      (id) => allRowSelectionObj[id] && !existingReferenceIds.has(Number(id))
    ).length
  }, [allRowSelectionObj, existingReferenceIds])

  // Total count excluding already-linked items — used for pagination and display
  const totalAvailableCount = useMemo(
    () => Math.max(0, totalElements - existingReferenceIds.size),
    [totalElements, existingReferenceIds]
  )

  const selectedDisplayCount = isAllDataSelected ? totalAvailableCount : selectedCount

  // Memoize objects passed as props to DataGrid to prevent new object identity every render,
  // which would trigger DataGrid's internal useEffect and cause infinite setState loops.
  const gridState = useMemo(
    () => ({ pagination, sorting, rowSelection: allRowSelectionObj }),
    [pagination, sorting, allRowSelectionObj]
  )

  const gridOptions = useMemo(
    () => ({
      enableRowSelection: (row: any) => !isCurrentCriteriaFullyLinked && !existingReferenceIds.has(row.original?.id)
    }),
    [existingReferenceIds, isCurrentCriteriaFullyLinked]
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onClose?.()
  }, [onClose])

  const handleConfirm = useCallback(async () => {
    setReferenceError(null)
    try {
      const payload = isAllDataSelected
        ? []
        : Object.keys(allRowSelectionObj)
            .filter((id) => allRowSelectionObj[id] && !existingReferenceIds.has(Number(id)))
            .map((id) => ({
              filename: '',
              referenceFsiId: Number(id),
              referenceType: 'DOCUMENT',
              obsolete: false
            }))

      const selectAllCriteria =
        isAllDataSelected && appliedSearchCriteria
          ? {
              ...appliedSearchCriteria,
              excludeFsiId: fsiId
            }
          : undefined

      if (requireESign) {
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_UPDATE_REFERENCES')
        await addDocumentRefs(fsiId, payload, validation, selectAllCriteria)
      } else {
        await addDocumentRefs(fsiId, payload, undefined, selectAllCriteria)
      }

      const successCount = isAllDataSelected
        ? Math.max(0, totalElementsRef.current - existingReferenceIds.size)
        : payload.length
      toast.success(`${successCount} selected references added successfully`, 'bottom-right')
      setReferenceError(null)

      if (isAllDataSelected && currentCriteriaKey) {
        // Mark this criteria as fully linked so rows stay disabled across pagination.
        setFullyLinkedCriteriaKey(currentCriteriaKey)
      }

      // Update local linked ids immediately so newly-added references become disabled
      // without waiting for the parent modal to re-render.
      const addedIds = isAllDataSelected
        ? references.filter((ref) => !existingReferenceIds.has(ref.id)).map((ref) => ref.id)
        : payload.map((item) => item.referenceFsiId)
      if (addedIds.length > 0) {
        setLocalExistingLinkedIds((prev) => Array.from(new Set([...prev, ...addedIds])))
      }

      onConfirm(selectedReferences)
      setRowSelectionByPages({})
      selectedItemsMapRef.current.clear()
      setIsAllDataSelected(false)
    } catch (error: any) {
      console.error('[Add ReferencesModal] Failed to add references:', error)
      const errorMsg = error?.data?.message || error?.body?.message || error?.message || ''
      if (
        errorMsg.toLowerCase().includes('maximum') ||
        errorMsg.toLowerCase().includes('exceeds') ||
        error?.status === 400
      ) {
        setReferenceError(
          t('REFERENCES_MAX_EXCEEDED', {
            defaultValue: `The number of documents selected (${selectedDisplayCount}) exceeds the maximum number of References allowed (${MAX_REFERENCES})`
          })
        )
      } else if (error?.status === 403) {
        toast.error(errorMsg)
      } else {
        toast.error(t('ERROR', { defaultValue: 'An error occurred' }))
      }
    }
  }, [
    allRowSelectionObj,
    existingReferenceIds,
    references,
    selectedReferences,
    selectedCount,
    fsiId,
    requireESign,
    signAction,
    onConfirm,
    t,
    isAllDataSelected,
    appliedSearchCriteria,
    currentCriteriaKey,
    selectedDisplayCount
  ])

  // DataGrid handlers — only update state; the reactive useEffect handles fetching.
  const handlePaginationChange = useCallback((newPagination: { pageIndex: number; pageSize: number }) => {
    setPagination(newPagination)
  }, [])

  const handleSortingChange = useCallback((newSorting: Array<{ id: string; desc: boolean }>) => {
    // Mark that the next onRowSelectionChange({}) is from DataGrid's internal
    // clear (not a user action), so handleRowSelectionChange can skip it.
    // Only set when there are actual selections — otherwise the flag stays stale
    // because DataGrid's setRowSelection({}) is a no-op when already empty.
    if (Object.keys(allRowSelectionRef.current).length > 0) {
      dgInternalClearRef.current = true
    }
    setSorting((prev) => {
      const isSameSorting =
        prev.length === newSorting.length &&
        prev.every((item, index) => item.id === newSorting[index]?.id && item.desc === newSorting[index]?.desc)

      return isSameSorting ? prev : newSorting
    })
  }, [])

  const handleDataGridControlChange = useCallback((state: any) => {
    if (state.pagination) {
      // Mark that the next onRowSelectionChange({}) is from DataGrid's internal
      // clear (not a user action), so handleRowSelectionChange can skip it.
      // Only set when there are actual selections — otherwise the flag stays stale
      // because DataGrid's setRowSelection({}) is a no-op when already empty.
      if (Object.keys(allRowSelectionRef.current).length > 0) {
        dgInternalClearRef.current = true
      }
      setPagination(state.pagination)
    }
  }, [])

  const safePageCount = useMemo(() => {
    const pageSize =
      Number.isFinite(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : DEFAULT_PAGE_SIZE
    return Math.max(1, Math.ceil((totalAvailableCount || 0) / pageSize))
  }, [totalAvailableCount, pagination.pageSize])

  const gridColumns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('DOCUMENT_NAME'),
        id: 'name',
        textAlign: 'left',
        size: 350,
        enableSorting: true,
        cell: ({ cell, row }: any) => (
          <a
            href={buildUrlWithCSelections(`/show-file/${row.original.id}`)}
            target="_blank"
            rel="noopener noreferrer"
            title={cell.getValue()}
          >
            {cell.getValue()}
          </a>
        )
      }),
      columnHelper.accessor('artifact', {
        header: t('TABLE_TH_ARTIFACT_NO'),
        id: 'artifact',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('product', {
        header: t('EMTF_MD_PRODUCT'),
        id: 'product',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('study', {
        header: t('EMTF_MD_STUDY'),
        id: 'study',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('environment', {
        header: t('EMTF_MD_ENVIRONMENT'),
        id: 'environment',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('country', {
        header: t('EMTF_MD_COUNTRY'),
        id: 'country',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('site', {
        header: t('EMTF_MD_SITE'),
        id: 'site',
        textAlign: 'left',
        enableSorting: true
      })
    ],
    [t]
  )

  // Check if any filter is active (for showing "Clear filters")
  const hasActiveFilters = !!(
    productFilter ||
    studyFilter ||
    environmentFilter ||
    countryFilter ||
    siteFilter ||
    (artifactFilter && artifactFilter.length > 0)
  )

  // Apply button is only enabled if user has entered search text or selected at least one dropdown
  const isApplyEnabled = !!(
    searchValue.trim() ||
    productFilter ||
    studyFilter ||
    environmentFilter ||
    countryFilter ||
    siteFilter ||
    (artifactFilter && artifactFilter.length > 0)
  )

  // RBAC: If user is read-only, show a message
  if (isReadOnly) {
    return (
      <Modal show={isOpen} handleClose={handleClose} size="full" className={styles.modalBodyFlex}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <MedidataLogo />
          <Modal.Title className={styles.modalTitleCentered}>
            {t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '16px' }}>
            <EmptyState
              variant="card"
              defaultImgVariant="noData"
              title={t('REFERENCES_NO_PERMISSION', { defaultValue: 'You do not have permission to add references' })}
              subtitle={t('REFERENCES_NO_PERMISSION_SUBTITLE', {
                defaultValue: 'Contact your administrator for access.'
              })}
            />
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  // Workflow check: if editing in workflow is not allowed
  if (!allowEditInWorkflow) {
    return (
      <Modal show={isOpen} handleClose={handleClose} size="full" className={styles.modalBodyFlex}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <MedidataLogo />
          <Modal.Title className={styles.modalTitleCentered}>
            {t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '16px' }}>
            <EmptyState
              variant="card"
              defaultImgVariant="noData"
              title={t('REFERENCES_WORKFLOW_RESTRICTED', {
                defaultValue: 'Editing is not allowed during this workflow phase'
              })}
              subtitle={t('REFERENCES_WORKFLOW_RESTRICTED_SUBTITLE', {
                defaultValue:
                  'The "Allow Editing of Property Values within Edit and Review Phase" setting is disabled for this document type.'
              })}
            />
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal show={isOpen} handleClose={handleClose} size="full" className={styles.modalBodyFlex}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <MedidataLogo />
        <Modal.Title className={styles.modalTitleCentered}>
          {t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={`theme-sandman ${styles.addReferencesDataGrid}`} style={{ padding: '16px' }}>
          <Search
            size="sm"
            value={searchValue}
            onChange={(value: string) => {
              setSearchValue(value)
              setRowSelectionByPages({})
              selectedItemsMapRef.current.clear()
              setIsAllDataSelected(false)
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && isApplyEnabled) executeSearch()
            }}
            onClear={handleClearSearch}
            showClearBtn={!!searchValue}
            maxLength={255}
            placeholder={t('REFERENCES_SEARCH_PLACEHOLDER', { defaultValue: 'Search' })}
            className="mb-3"
          />

          <div className={styles.filterBar}>
            <FilterPanel className="align-items-center">
              <FilterPanel.Filters>
                <FilterSelect
                  size="sm"
                  label={t('EMTF_MD_PRODUCT')}
                  displayApplyButton={false}
                  isSearchable
                  options={filterOptions.product}
                  value={productFilter ? [productFilter] : []}
                  onChange={handleFilterChange(setProductFilter)}
                />
                <FilterSelect
                  size="sm"
                  label={t('EMTF_MD_STUDY')}
                  displayApplyButton={false}
                  isSearchable
                  options={filterOptions.study}
                  value={studyFilter ? [studyFilter] : []}
                  onChange={handleFilterChange(setStudyFilter)}
                  disabled={!productFilter}
                />
                <FilterSelect
                  size="sm"
                  label={t('EMTF_MD_ENVIRONMENT')}
                  displayApplyButton={false}
                  isSearchable
                  options={filterOptions.environment}
                  value={environmentFilter ? [environmentFilter] : []}
                  onChange={handleFilterChange(setEnvironmentFilter)}
                  disabled={!studyFilter}
                />
                <FilterSelect
                  size="sm"
                  label={t('EMTF_MD_COUNTRY')}
                  displayApplyButton={false}
                  isSearchable
                  options={filterOptions.country}
                  value={countryFilter ? [countryFilter] : []}
                  onChange={handleFilterChange(setCountryFilter)}
                  disabled={!environmentFilter}
                />
                <FilterSelect
                  size="sm"
                  label={t('EMTF_MD_SITE')}
                  displayApplyButton={false}
                  isSearchable
                  options={filterOptions.site}
                  value={siteFilter ? [siteFilter] : []}
                  onChange={handleFilterChange(setSiteFilter)}
                  disabled={!countryFilter}
                />
                <FilterSelect
                  size="sm"
                  label={t('TABLE_TH_ARTIFACT_NO')}
                  displayApplyButton={false}
                  isSearchable
                  options={artifactOptions}
                  value={artifactFilter || []}
                  isMulti
                  onChange={(option: any) => {
                    setArtifactFilter(option || [])
                    setRowSelectionByPages({})
                    selectedItemsMapRef.current.clear()
                    setIsAllDataSelected(false)
                  }}
                />
              </FilterPanel.Filters>
              <FilterPanel.EndButtons>
                {hasActiveFilters && (
                  <Button variant="critical" size="sm" onClick={handleClearFilters} buttonStyle="ghost">
                    {t('CLEAR_FILTERS', { defaultValue: 'Clear filters' })}
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={handleApply} disabled={!isApplyEnabled}>
                  {t('APPLY', { defaultValue: 'Apply' })}
                </Button>
              </FilterPanel.EndButtons>
            </FilterPanel>
          </div>

          {referenceError && (
            <div className="alert alert-danger mb-3 " role="alert">
              <Icon className="me-2" style={{ color: 'var(--bs-danger, #dc3545)', fontSize: '1.25rem' }}>
                error
              </Icon>
              {referenceError}
            </div>
          )}

          {!hasSearched && references.length === 0 ? (
            <div
              style={{
                border: '1px solid var(--border-color-default, #e0e0e0)',
                borderRadius: '8px',
                padding: '80px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px'
              }}
            >
              <EmptyState
                variant="card"
                defaultImgVariant="noData"
                title={t('REFERENCES_EMPTY_TITLE', { defaultValue: 'No references to show' })}
                subtitle={t('REFERENCES_EMPTY_SUBTITLE', {
                  defaultValue: 'Search for documents to add as references by using the menus above'
                })}
              />
            </div>
          ) : (
            <>
              <div className="reference-search-datagrid">
                <PropertySearchBulkActionHeader
                  selectedCount={selectedCount}
                  isAllDataSelected={isAllDataSelected}
                  totalCount={totalAvailableCount}
                  isCountLoading={false}
                  onSelectAll={handleSelectAllForCurrentCriteria}
                  onClearSelection={handleClearSelection}
                />

                <DataGrid
                  isLoading={isLoading}
                  dataTestId="add-references-datagrid"
                  columns={gridColumns}
                  data={references}
                  enablePagination={true}
                  manualPagination={true}
                  pagination={pagination}
                  manualDataLength={totalAvailableCount}
                  onPaginationChange={handlePaginationChange}
                  onChangeControl={handleDataGridControlChange}
                  pageCount={safePageCount}
                  rowsPerPageOptions={PAGE_SIZES_ARRAY}
                  title={t('REFERENCES_AVAILABLE_TITLE', { defaultValue: 'Available references' })}
                  enableGlobalFilter={false}
                  enableGlobalSearch={false}
                  enableRowSelection={true}
                  options={gridOptions}
                  getRowId={(row: AvailableReference) => String(row.id)}
                  onRowSelectionChange={handleRowSelectionChange}
                  onAllDataSelected={handleOnAllDataSelected}
                  stickySelection={true}
                  enableSorting={true}
                  manualSorting={true}
                  onSortingChange={handleSortingChange}
                  emptyStateOptions={{
                    defaultImgVariant: 'noData',
                    title: t('REFERENCES_EMPTY_TITLE', { defaultValue: 'No references to show' }),
                    subtitle: t('REFERENCES_EMPTY_SUBTITLE', {
                      defaultValue: 'Search for documents to add as references by using the menus above'
                    })
                  }}
                  isCursorPagination={false}
                  dataGridControlProps={{
                    hasActionBtn: false,
                    hasViewOptions: false,
                    hasSecondaryActions: false
                  }}
                  className="add-references-datagrid"
                  state={gridState}
                />
              </div>
            </>
          )}

          <div className={styles.footerBar}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={selectedCount === 0 && !isAllDataSelected}
              >
                {t('REFERENCES_ADD_SELECTED', { defaultValue: 'Add selected' })}
              </Button>
              {selectedDisplayCount > 0 && (
                <span className={styles.badgeOverlay}>
                  <BadgeCounter variant="caution">{selectedDisplayCount}</BadgeCounter>
                </span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default AddReferencesModal
